import React, { useEffect, useState, useRef, useCallback } from "react";
import ScheduleBox from "./ScheduleBox";
import { GrSearch } from "react-icons/gr";
import { FaFilter, FaSpinner, FaTimes } from "react-icons/fa";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import { AppDispatch, checkSessionStatus, getSessions, getSessionsByAutoSearch, markSessionAsViewed, sessionBatch, verifyOfficialSession } from "../../store";
import debounce from "lodash.debounce";
import { collection, doc, getDoc, onSnapshot, query, where } from "firebase/firestore";
import DateInputWithIcon from "./DateInput";
import { ReactComponent as DownIcon } from "../../assets/down-icon.svg";
import { ReactComponent as DropdownIcon } from "../../assets/chevron-down.svg";
import { finishedAtSortFn, scheduledAtSortFn, scheduledAtSortLatestFirstFn, toCamelCase } from "../../utils/helper";
import { auth, database, db, ref } from "../../utils/firebaseConfig";
import { getFromLocalStorage } from "../../utils/localStorageUtil";
import { COMPLETED_STATUS } from "../../constants/sessions";
// import { signInWithCustomToken } from "firebase/auth";
// import { useFirebaseAuth } from "../../hooks/useFirebaseAuth";
// import { onValue } from "firebase/database";

interface Session {
  id: string;
  title: string;
  date: string;
  name: string;
  appointmentId: string;
}

interface Sessions {
  scheduled: Session[];
  completed: Session[];
}

export interface NextPageCursor {
  scheduled: string;
  completed: string;
}

const SessionTimeline: React.FC<{
  onSessionSelect: (session: any) => void;
  activeSessionId?: string;
  onTabChange: (activeTab: "scheduled" | "completed") => void;
}> = ({ onSessionSelect, activeSessionId, onTabChange }) => {
  const dispatch = useDispatch<AppDispatch>();
  const isMounted = useRef(true);
  const [nextPageCursor, setNextPageCursor] = useState<NextPageCursor>({
    scheduled: "",
    completed: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const [selectedOption, setSelectedOption] = useState<string>("name");
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [initialScheduleSessionLoading, setInitialScheduleSessionLoading] = useState(true);
  const [initialCompletedSessionLoading, setInitialCompletedSessionLoading] = useState(true);
  const [fetchingMore, setFetchingMore] = useState<boolean>(false);
  const [firebaseReady, setFirebaseReady] = useState(false);

  const activeSessionIdRef = useRef(activeSessionId);
  const suggestionBoxRef = useRef<HTMLDivElement>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const intervalId = useRef<ReturnType<typeof setInterval> | null>(null);

  const [activeTab, setActiveTab] = useState<"scheduled" | "completed">(
    "scheduled"
  );
  const handleTabChange = (tab: "scheduled" | "completed") => {
    setSearchTerm("");
    setShowFilters(false);
    setShowSuggestions(false)
    setActiveTab(tab);
    onTabChange(tab);
    onSessionSelect(sessions[tab][0]);
    setSelectedBox(0);

  };
  const [searchNextPageCursor, setSearchNextPageCursor] = useState<NextPageCursor>({
    scheduled: "",
    completed: "",
  });
  const isInitialSnapshot = useRef(true);

  // Tracks sessions whose active->completed transition is being confirmed/moved,
  // so duplicate snapshot drop-out signals don't trigger repeated moves (NFR-3).
  const movingSessionsRef = useRef<Set<string>>(new Set());
  // Error/retry state for reflecting a just-completed session in the Completed list (FR-10).
  const [completedReflectError, setCompletedReflectError] = useState(false);
  const lastReflectFnRef = useRef<(() => void) | null>(null);

  const [selectedBox, setSelectedBox] = useState<number | null>(null);
  const [sessions, setSessions] = useState<Sessions>({
    scheduled: [],
    completed: [],
  });
  // Always-current mirror of `sessions` for synchronous reads from async callbacks
  // (the onSnapshot listener). React may invoke state updaters lazily / more than once,
  // so we must NOT compute side-effect data (drop-out detection, completed-list indexing)
  // inside a setSessions updater — read from this ref instead.
  const sessionsRef = useRef<Sessions>({ scheduled: [], completed: [] });
  // const [batchSessions, setBatchSessions] = useState<Sessions>({
  //   scheduled: [],
  //   completed: [],
  // });
  const [searchSessions, setSearchSessions] = useState<Sessions>({
    scheduled: [],
    completed: [],
  });
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isSearching, setIsSearching] = useState(false);

  const [showFilters, setShowFilters] = useState<boolean>(false);
  const sessionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Keep the synchronous mirror in lockstep with committed `sessions` state.
  useEffect(() => {
    sessionsRef.current = sessions;
  }, [sessions]);

  const mergeUniqueById = (existing: any[] = [], incoming: any[] = []) => {
    const merged = [...existing];
    const existingIds = new Set(existing.map((item: any) => item?.id));

    incoming.forEach((item: any) => {
      if (!existingIds.has(item?.id)) {
        merged.push(item);
      }
    });

    return merged;
  };

  const sortForTab = (items: any[] = [], tab: "scheduled" | "completed") => {
    if (tab === "scheduled") {
      return [...items].sort(scheduledAtSortFn);
    }

    return [...items].sort(finishedAtSortFn);
  };

  const getNextCursorFromResponse = (items: any[] = []) => {
    for (let i = items.length - 1; i >= 0; i--) {
      if (items[i]?.nextPageCursor) {
        return items[i].nextPageCursor;
      }
    }

    return "";
  };

  const handleClickOutsideDropdown = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setShowDropdown(false);
    }
  };

  useEffect(() => {
    (async () => {
      const officialUid = getFromLocalStorage("officialId");
      const rolesFromStorage = getFromLocalStorage("roles");
      const roles = Array.isArray(rolesFromStorage)
        ? rolesFromStorage.join(',')
        : '';
      // const roles = ["notaryAdmin", "scheduler"];
      if (officialUid) {
        const response = await dispatch(
          verifyOfficialSession({
            uid: officialUid,
            roles
          })
        );
        if (response.meta.requestStatus === "fulfilled") {
          setFirebaseReady(true);
        }
      }
    })()
  }, []);

  // Confirm a dropped-out session actually completed (vs cancelled/deleted/transient),
  // then move it from the scheduled list into the completed list (FR-1/2/3, FR-8).
  // Reflecting in the completed list is optimistic-insert + a reconciling refetch with
  // loading/error+retry handling (FR-10). This story does NOT switch the operator's
  // tab/selection — that is Story 1.2.
  const moveCompletedSession = useCallback(
    async (droppedSession: any) => {
      const sessionId = droppedSession?.id;
      if (!sessionId) return;
      // NFR-3: never run the move twice for the same session concurrently.
      if (movingSessionsRef.current.has(sessionId)) return;
      movingSessionsRef.current.add(sessionId);

      try {
        // FR-8: trust the backend status field, not mere absence from the active snapshot.
        const statusResponse = await dispatch(
          checkSessionStatus({ sessionId })
        );
        if (statusResponse.meta.requestStatus !== "fulfilled") {
          return; // could not confirm -> leave it in scheduled, do not move
        }
        const confirmedSession = statusResponse.payload;
        if (confirmedSession?.status !== COMPLETED_STATUS) {
          return; // cancelled/transient/other -> not a completion, do not move (FR-8)
        }

        const movedSession = { ...droppedSession, ...confirmedSession };

        // FR-2 + FR-3 + NFR-2/3: remove from scheduled, insert (deduped) into completed.
        // Compute the post-merge completed list OUTSIDE the updater (from the synchronous
        // mirror) so Story 1.2's auto-switch can index into the exact array the UI renders.
        // Updaters may run lazily/twice, so never read values back out of them.
        const mergedCompleted = sortForTab(
          mergeUniqueById(sessionsRef.current.completed, [movedSession]),
          "completed"
        );
        setSessions((prev) => {
          const scheduled = prev.scheduled.filter(
            (s: any) => s?.id !== sessionId
          );
          const completed = sortForTab(
            mergeUniqueById(prev.completed, [movedSession]),
            "completed"
          );
          return { ...prev, scheduled, completed };
        });

        // Story 1.2 — conditional auto-switch (FR-4/FR-5/FR-7/FR-9, NFR-1).
        // Only carry the operator to the completed session if they are *viewing* the
        // session that just completed. Use activeSessionIdRef (the live, closure-safe
        // current value), NOT the activeSessionId prop, which is stale inside this
        // async callback. When NOT viewing, do nothing extra — the move above already
        // removed it from the active list silently (FR-5).
        if (movedSession?.id === activeSessionIdRef.current) {
          // Clear any active Completed-tab filter so the auto-selected session can't be
          // filtered out of view (FR-9 / AC 4) — mirrors handleTabChange's reset.
          setSearchTerm("");
          setShowFilters(false);
          setShowSuggestions(false);
          // Flip both the local list tab (UI) AND lift to home.tsx via onTabChange —
          // the latter drives activeScheduleTab in SessionSummary, which selects the
          // completed-action dropdown (FR-6, satisfied for free once this fires).
          setActiveTab("completed");
          onTabChange("completed");
          // Keep the just-completed session selected and highlighted. Index into the
          // post-merge completed array so the highlighted box matches the selection.
          onSessionSelect(movedSession);
          const movedIndex = mergedCompleted.findIndex(
            (s: any) => s?.id === movedSession?.id
          );
          setSelectedBox(movedIndex === -1 ? 0 : movedIndex);
        }

        // FR-3 reconciliation: refetch the completed list so it matches the backend
        // (cursor/ordering). FR-10: surface a loading state and an error+retry on failure.
        const reflect = () => {
          setCompletedReflectError(false);
          setInitialCompletedSessionLoading(true);
          dispatch(getSessions({ status: "completed", search: "" }))
            .then((completedResponse: any) => {
              if (completedResponse.meta.requestStatus === "fulfilled") {
                const responseData = [
                  ...(completedResponse.payload?.data || []),
                ];
                const reverseResponse = [...responseData].reverse();
                const tempCompleted = [...reverseResponse].sort(finishedAtSortFn);
                setSessions((prev: any) => ({
                  ...prev,
                  // keep the optimistic entry if the refetch hasn't surfaced it yet (NFR-2)
                  completed: sortForTab(
                    mergeUniqueById(tempCompleted, [movedSession]),
                    "completed"
                  ),
                }));
                if (responseData.length > 0) {
                  setNextPageCursor((prev) => ({
                    ...prev,
                    completed: getNextCursorFromResponse(responseData) || "",
                  }));
                }
                setCompletedReflectError(false);
              } else {
                setCompletedReflectError(true);
              }
            })
            .catch(() => {
              setCompletedReflectError(true);
            })
            .finally(() => {
              setInitialCompletedSessionLoading(false);
            });
        };
        lastReflectFnRef.current = reflect;
        reflect();
      } catch (error) {
        console.error("Error moving completed session:", error);
      } finally {
        movingSessionsRef.current.delete(sessionId);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (!firebaseReady || !auth.currentUser?.uid) return;
    const roles = getFromLocalStorage("roles");
    let q;
    if (roles?.includes("notaryAdmin") || roles?.includes("notaryScheduling") || roles?.includes("scheduler")) {
      q = query(
        collection(db, "notary-sessions"),
        where("status", "==", "active")
      );
    }
    else {
      q = query(
        collection(db, "notary-sessions"),
        where("officialUid", "==", auth.currentUser.uid),
        where("status", "==", "active")
      );

    }
    const unsub = onSnapshot(q, (querySnapshot) => {
      const updatedSessions = querySnapshot.docs.map((doc) => {
        const data = doc.data();

        const normalized = {
          ...data,
          scheduledAt: data.scheduledAt
            ? {
              _seconds: data.scheduledAt.seconds,
              _nanoseconds: data.scheduledAt.nanoseconds,
            }
            : null,
          signerLastOnlineAt: data?.signerLastOnlineAt
            ? {
              _seconds: data.signerLastOnlineAt.seconds,
              _nanoseconds: data.signerLastOnlineAt.nanoseconds,
            }
            : null,
          officialLastOnlineAt: data?.officialLastOnlineAt
            ? {
              _seconds: data.officialLastOnlineAt.seconds,
              _nanoseconds: data.officialLastOnlineAt.nanoseconds,
            }
            : null,
          observerLastOnlineAt: data?.observerLastOnlineAt
            ? {
              _seconds: data.observerLastOnlineAt.seconds,
              _nanoseconds: data.observerLastOnlineAt.nanoseconds,
            }
            : null,
        };

        return { id: doc?.id, ...normalized };
      });

      if (isInitialSnapshot.current) {
        isInitialSnapshot.current = false;
        return;
      }

      // IDs still present in the active snapshot this tick.
      const activeIds = new Set(updatedSessions.map((s) => s?.id));

      // Read the CURRENT scheduled list from the synchronous mirror — NOT from inside a
      // setSessions updater. React may run updaters lazily / more than once, so computing
      // drop-outs (a side effect) in there both fails to run reliably and double-counts.
      const currentScheduled = sessionsRef.current.scheduled || [];

      // Drop-outs: sessions present in our scheduled list but absent from the active
      // snapshot now (completed / cancelled / deleted / transient). Confirm each via the
      // backend status before moving (FR-1/FR-8). Skip ones already being moved (NFR-3).
      const droppedCandidates = currentScheduled.filter(
        (existing: any) =>
          existing?.id &&
          !activeIds.has(existing.id) &&
          !movingSessionsRef.current.has(existing.id)
      );

      // Pure updater: only patch live field updates onto still-active sessions
      // (and keep the open active session re-selected — NFR-4, unchanged).
      setSessions((prev) => {
        const currentList = prev.scheduled;
        const updatedList = [...currentList];

        updatedSessions.forEach((newSession) => {
          const index = currentList.findIndex((s) => s?.id === newSession?.id);
          if (index !== -1) {
            updatedList[index] = {
              ...currentList[index],
              ...newSession,
            };
            if (newSession?.id === activeSessionIdRef.current) {
              onSessionSelect(updatedList[index]);
              setSelectedBox(index);
            }
          }
        });

        return {
          ...prev,
          scheduled: updatedList,
        };
      });

      // Confirm + move each drop-out outside the state updater (async, side-effecting).
      droppedCandidates.forEach((dropped) => {
        moveCompletedSession(dropped);
      });
    });

    return () => unsub();
  }, [firebaseReady, moveCompletedSession, dispatch]);

  const handleBoxClick = async (item: any) => {
    setShowSuggestions(false);
    const sessionMarkedAsViewed = dispatch(
      markSessionAsViewed({
        sessionId: item?.id
      })
    );

    const activeSession = sessions[activeTab].find(
      (session: any) => session?.id.toString() === item?.id
    );
    if (activeSession) {
      sessionRefs.current[item?.id]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      onSessionSelect(activeSession);
      setSelectedBox(sessions[activeTab].indexOf(activeSession));
    }
  };



  const handleDropdownToggle = () => setShowDropdown((prev) => !prev);
  const handleOptionSelect = (option: string) => {
    setSelectedOption(option);
    setShowDropdown(false);
  };

  const fetchSessions = useCallback(
    debounce(
      async (
        search: string,
        tab: "scheduled" | "completed",
        selectedOption: string,
        sessions: Sessions,
        nextPageCursor?: NextPageCursor
      ) => {
        try {
          setIsLoading(true);

          if (search) {
            const response = await dispatch(
              getSessionsByAutoSearch({
                type:
                  selectedOption === "Appt ID"
                    ? "appointmentId"
                    : selectedOption === "notary"
                      ? "official"
                      : selectedOption,
                search,
                status: tab === "scheduled" ? "active" : "completed",
                cursor: nextPageCursor?.[tab] || "",
              })
            );

            if (response.meta.requestStatus === "fulfilled") {
              const responseData = [...(response?.payload?.data || [])];
              const tempScheduled = [...responseData].sort(scheduledAtSortLatestFirstFn);
              setSessions((prev) => ({
                ...prev,
                [tab]: nextPageCursor?.[tab]
                  ? [...prev[tab], ...tempScheduled].sort(scheduledAtSortLatestFirstFn)
                  : tempScheduled,
              }));
              let tempSessions = {
                ...sessions,
                [tab]: nextPageCursor?.[tab]
                  ? [...sessions[tab], ...tempScheduled].sort(scheduledAtSortLatestFirstFn)
                  : tempScheduled,
              }
              const shouldActiveSession = tempSessions[activeTab].find(item => item.id === activeSessionId)
              if(!shouldActiveSession){
                onSessionSelect(tempSessions[activeTab][0]);
                setSelectedBox(0);
              }
              if (responseData.length > 0) {
                const updatedNextCursor = getNextCursorFromResponse(responseData);
                setSearchNextPageCursor((prev) => ({
                  ...prev,
                  [tab]: updatedNextCursor || "",
                }));
              } else {
                setSearchNextPageCursor((prev) => ({
                  ...prev,
                  [tab]: "",
                }));
              }
            }
          }
          else {
            if (nextPageCursor?.[tab]) {
              const loadMoreResponse = await dispatch(
                getSessions({
                  status: tab,
                  search: "",
                  cursor: nextPageCursor[tab],
                })
              );
              if (loadMoreResponse.meta.requestStatus === "fulfilled") {
                const newSessions = loadMoreResponse.payload?.data || [];

                setSessions((prev) => ({
                  ...prev,
                  [tab]: sortForTab(mergeUniqueById(prev[tab], newSessions), tab),
                }));

                if (newSessions?.length > 0) {
                  const updatedNextCursor = getNextCursorFromResponse(newSessions);
                  setNextPageCursor((prev) => ({
                    ...prev,
                    [tab]: updatedNextCursor || "",
                  }));
                } else {
                  setNextPageCursor((prev) => ({
                    ...prev,
                    [tab]: "",
                  }));
                }
              }
            } else {

              const scheduledResponse = await dispatch(getSessions({ status: "scheduled", search: "" }));
              let foundSessions = {
                ...sessions,
              }
              let tempNextPageCursor: NextPageCursor = {
                completed: "",
                scheduled: ""
              };
              let shouldActiveScedulledSession = false
              let shouldActiveCompletedSession = false
              if (scheduledResponse.meta.requestStatus === "fulfilled") {
                const responseData = [...(scheduledResponse.payload?.data || [])];
                const reverseResponse = [...responseData].reverse();
                const tempScheduled = [...reverseResponse].sort(scheduledAtSortFn);

                foundSessions = {
                  ...foundSessions,
                  scheduled: [...(scheduledResponse.payload?.data || [])]
                };
                setSessions((prev: any) => ({
                  ...prev,
                  scheduled: tempScheduled,
                }));
                if (responseData.length > 0) {
                  tempNextPageCursor = {
                    ...tempNextPageCursor,
                    scheduled: getNextCursorFromResponse(responseData),
                  };
                  setNextPageCursor({
                    ...nextPageCursor,
                    ...tempNextPageCursor
                  })
                } else {
                  tempNextPageCursor = {
                    ...tempNextPageCursor,
                    scheduled: ""
                  };
                  setNextPageCursor({
                    ...nextPageCursor,
                    ...tempNextPageCursor
                  })
                }
                let tempSessions={
                  ...sessions,
                  scheduled: tempScheduled,
                }
                shouldActiveScedulledSession = tempSessions[activeTab].find(item => item.id === activeSessionId);
                if (!shouldActiveScedulledSession && activeSessionId) {
                  const checkSessionStatusResponse = await dispatch(
                    checkSessionStatus({
                      sessionId: activeSessionId,
                    })
                  );
                  if (checkSessionStatusResponse.meta.requestStatus === "fulfilled") {
                    const session = checkSessionStatusResponse.payload;
                    // Deep-linked to a session that isn't in the scheduled list: if it's
                    // already completed, flip to the Completed tab so the correct (completed)
                    // dropdown/action set renders. Otherwise leave the tab as-is.
                    if (session?.status === COMPLETED_STATUS && activeTab !== "completed") {
                      setActiveTab("completed");
                      onTabChange("completed");
                    }
                    onSessionSelect(session);
                  }
                }
              }
              setInitialScheduleSessionLoading(false)
              const completedResponse = await dispatch(getSessions({ status: "completed", search: "" }));

              if (completedResponse.meta.requestStatus === "fulfilled") {
                const responseData = [...(completedResponse.payload?.data || [])];
                const reverseResponse = [...responseData].reverse();
                const tempCompleted = [...reverseResponse].sort(finishedAtSortFn);


                foundSessions = {
                  ...foundSessions,
                  completed: [...(completedResponse.payload?.data || [])],
                }

                setSessions((prev: any) => ({
                  ...prev,
                  completed: tempCompleted,
                }));
                if (responseData.length > 0) {
                  tempNextPageCursor = {
                    ...tempNextPageCursor,
                    completed: getNextCursorFromResponse(responseData),
                  }
                  setNextPageCursor({
                    ...nextPageCursor,
                    ...tempNextPageCursor
                  })
                } else {
                  tempNextPageCursor = { ...tempNextPageCursor, completed: "" }
                  setNextPageCursor({
                    ...nextPageCursor,
                    ...tempNextPageCursor
                  })

                }
                let tempSessions = {
                  ...sessions,
                  completed: tempCompleted,
                }
                shouldActiveCompletedSession = tempSessions[activeTab].find(item => item.id === activeSessionId);
                if (!shouldActiveCompletedSession && activeSessionId && !shouldActiveScedulledSession) {
                  const checkSessionStatusResponse = await dispatch(
                    checkSessionStatus({
                      sessionId: activeSessionId,
                    })
                  );
                  if (checkSessionStatusResponse.meta.requestStatus === "fulfilled") {
                    const session = checkSessionStatusResponse.payload;
                    // Deep-linked session found in neither list: flip to Completed if it's
                    // completed so the correct dropdown renders (mirrors the scheduled branch).
                    if (session?.status === COMPLETED_STATUS && activeTab !== "completed") {
                      setActiveTab("completed");
                      onTabChange("completed");
                    }
                    onSessionSelect(session);
                  }
                }
                setInitialCompletedSessionLoading(false);
              }
            }
          }
        } catch (error) {
          console.error("Error fetching sessions:", error);
        } finally {
          setInitialScheduleSessionLoading(false);
          setInitialCompletedSessionLoading(false);
          setFetchingMore(false);
          setIsLoading(false);
          setIsSearching(false);
        }
      },
      300
    ),
    [dispatch, activeSessionId, sessions]
  );

  const handleClickOutside = (event: MouseEvent) => {
    if (
      suggestionBoxRef.current &&
      !suggestionBoxRef.current.contains(event.target as Node)
    ) {
      setShowSuggestions(false);
    }
  };

  const handleLoadMore = () => {
    if (isLoading || fetchingMore) {
      return;
    }

    if (searchTerm.trim()) {
      if (searchNextPageCursor[activeTab]) {
        setFetchingMore(true);
        fetchSessions(
          searchTerm,
          activeTab,
          selectedOption,
          sessions,
          searchNextPageCursor
        );
      }
    } else if (nextPageCursor[activeTab]) {
      setFetchingMore(true);
      fetchSessions("", activeTab, selectedOption, sessions, nextPageCursor);
    }
  };

  useEffect(() => {
    if (showSuggestions) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSuggestions]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutsideDropdown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideDropdown);
    };
  }, [showDropdown]);


  useEffect(() => {
    (async ()=>{
      activeSessionIdRef.current = activeSessionId;
      const activeSession = sessions[activeTab].find(
        (session: any) => session?.id.toString() === activeSessionId
      );
      if (activeSessionId && sessions[activeTab].length > 0 && activeSession) {
        onSessionSelect(activeSession);
        setSelectedBox(sessions[activeTab].indexOf(activeSession));
      }
      else if (activeSessionId) {
        // Direct-load / deep-link case: the active session id points at a session that
        // isn't in the CURRENT tab's list. The live auto-switch (moveCompletedSession)
        // only handles sessions that complete *while* we're watching them; a session
        // that is ALREADY completed when the page loads never passes through there.
        // So confirm its status and, if completed, flip to the Completed tab — otherwise
        // the dropdown (driven by activeScheduleTab) stays "scheduled" and shows the
        // wrong (active) action set on a completed session.
        const checkSessionStatusResponse = await dispatch(
          checkSessionStatus({ sessionId: activeSessionId })
        );
        if (checkSessionStatusResponse.meta.requestStatus === "fulfilled") {
          const confirmed = checkSessionStatusResponse.payload;
          if (confirmed?.status === COMPLETED_STATUS && activeTab !== "completed") {
            setActiveTab("completed");
            onTabChange("completed");
          }
          onSessionSelect(confirmed);
        }
      }
      else if (sessions[activeTab]?.length) {
        onSessionSelect(sessions[activeTab][0]);
        setSelectedBox(0);
  
      }
    })();

  }, [activeSessionId, sessions]);

  useEffect(() => {
    isMounted.current = true;

    (async () => {
      if (sessions[activeTab]?.length == 0) {
        setInitialScheduleSessionLoading(true)
        setInitialCompletedSessionLoading(true)
      }
      setSearchSessions({
        scheduled: [],
        completed: [],
      })

      fetchSessions(searchTerm, activeTab, selectedOption, sessions);

    })();
    return () => {
      isMounted.current = false;
      fetchSessions.cancel();
    };
  }, [searchTerm]);

  const handleClearSearch = () => {
    setSearchTerm("");
    fetchSessions("", activeTab, selectedOption, sessions);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value.trim()) {
      setShowDropdown(false)
      setIsSearching(true);
    } else {
      fetchSessions("", activeTab, selectedOption, sessions);
      setIsSearching(false);
    }
  };

  return (
    <SessionInfoBox>
      <Tabs>
        <Tab
          isActive={activeTab === "scheduled"}
          onClick={() => handleTabChange("scheduled")}
        >
          Scheduled
        </Tab>
        <Tab
          isActive={activeTab === "completed"}
          onClick={() => handleTabChange("completed")}
        >
          Completed
        </Tab>
      </Tabs>
      <SearchBox>
        <SearchInputAndDropdown>
          <DropdownContainer ref={dropdownRef}>
            <DropdownButton onClick={handleDropdownToggle}>
              {selectedOption === "name" ? "Principal" : toCamelCase(selectedOption) || "Search By"}
              <DownIcon />
            </DropdownButton>
            {showDropdown && (
              <DropdownMenu showDropdown={showDropdown}>
                <DropdownItem onClick={() => handleOptionSelect("name")}>Principal</DropdownItem>
                <DropdownItem onClick={() => handleOptionSelect("notary")}>Notary</DropdownItem>
                <DropdownItem onClick={() => handleOptionSelect("Appt ID")}>Appointment ID</DropdownItem>
                <DropdownItem onClick={() => handleOptionSelect("email")}>Email</DropdownItem>
              </DropdownMenu>
            )}
          </DropdownContainer>
          <SearchInput
            placeholder="Search..."
            value={searchTerm}
            onChange={handleSearchInputChange}
          />
          {searchTerm ? (
            <ClearIcon onClick={handleClearSearch} />
          ) : (
            isSearching ? <LoadingIcon /> : <SearchIcon />)}
        </SearchInputAndDropdown>
      </SearchBox>

      <ScheduleBoxContainer isLoading={false}>
        {activeTab == "completed" &&
          completedReflectError &&
          !initialCompletedSessionLoading && (
            <ReflectErrorContainer>
              <ItemStatus>Couldn't refresh the completed list.</ItemStatus>
              <RetryButton
                onClick={() => lastReflectFnRef.current?.()}
              >
                Retry
              </RetryButton>
            </ReflectErrorContainer>
          )}
        {(activeTab == "scheduled" && initialScheduleSessionLoading) ||
          (activeTab == "completed" && initialCompletedSessionLoading) ? (
          <RefreshingListContainer>
            <RefreshingListLoader />
            <ItemStatus>Refreshing</ItemStatus>
          </RefreshingListContainer>
        ) : sessions[activeTab].length === 0 ? (
          <ItemStatus>No items found</ItemStatus>
        ) : (
          sessions[activeTab]?.map((item: any, index: number) => (
            <div
              key={item?.id}
            // ref={(el) => (sessionRefs.current[item.id] = el)}
            >
              <ScheduleBox
                key={index}
                isSelected={selectedBox === index}
                onClick={() => handleBoxClick(item)}
                item={item}
                activeTab={activeTab}
              />
            </div>
          ))
        )}
        {((searchTerm.trim() && searchNextPageCursor[activeTab]) ||
          (!searchTerm.trim() && nextPageCursor[activeTab])) &&
          !(activeTab == "scheduled" && initialScheduleSessionLoading) &&
          !(activeTab == "completed" && initialCompletedSessionLoading) && (
            <ButtonBox>
              {fetchingMore ? (
                <FetchMoreLoaderContainer>
                  <FetchMoreLoader />
                </FetchMoreLoaderContainer>
              ) : (
                <LoadMoreButton
                  disabled={isLoading}
                  onClick={handleLoadMore}
                >
                  {"Load More"}
                </LoadMoreButton>
              )}
            </ButtonBox>
          )}

      </ScheduleBoxContainer>
    </SessionInfoBox>
  );
};

export default SessionTimeline;

const ItemStatus = styled.div`
  text-align: center;
  color: #888;
  margin-top: 16px;
  font-size: 14.6px;
`;

const ReflectErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 8px;
`;

const RetryButton = styled.button`
  background: white;
  color: #2b70f4;
  border: 1px solid #2b70f4;
  font-size: 13px;
  border-radius: 4px;
  cursor: pointer;
  padding: 6px 18px;

  &:hover {
    background: #f0f6ff;
  }
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 40.5%;
`;

const RefreshingListContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const FetchMoreLoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 40.5%;
`;

const ScheduleBoxContainer = styled.div<{ isLoading: boolean }>`
  padding: 7.2px;
  display: ${(props) => (props.isLoading ? "flex" : "block")};
  justify-content: center;
  overflow-y: auto;
  flex: 1;
  cursor: pointer;
  padding-top: 0px;
  padding-bottom: 16.2px;
  ::-webkit-scrollbar {
    display: none;
  }
`;

const Tabs = styled.div`
  display: flex;
  border-bottom: 0.9px solid #ccc; /* Reduced */
  padding: 8.1px 16.2px;
  padding-top: 0px;
  justify-content: space-between;
`;

const Tab = styled.div<{ isActive: boolean }>`
  padding: 7px 14px;
  cursor: pointer;
  width: 45%;
  font-size: 15px;
  display: flex;
  justify-content: center;
  border-bottom: 2.7px solid
    ${(props) =>
    props.isActive ? props.theme.colors.secondary : "transparent"};
  color: ${(props) =>
    props.isActive ? props.theme.colors.secondary : "black"};
`;

const Loader = styled.div`
  border: 3.6px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 3.6px solid #13160b;
  width: 21.6px;
  height: 21.6px;
  animation: spin 2s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const RefreshingListLoader = styled.div`
  border: 3.6px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  margin-top: 50px;
  border-top: 3.6px solid #007bff;
  width: 26px;
  height: 26px;
  animation: spin 2s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const FetchMoreLoader = styled.div`
  border: 3.6px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 3.6px solid #2B70F4;
  width: 14.4px;
  height: 14.4px;
  animation: spin 2s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const SessionInfoBox = styled.div`
  width: 96.2%;
  background-color: #f9f9f9;
  padding: 12.2px 8.1px;
  padding-bottom: 0px;
  border-radius: 8.1px;
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const SearchBox = styled.div`
  position: relative;
  margin-bottom: 12.5px;
  padding: 3px 6px;
`;

const SearchInput = styled.input`
  width: 54%; /* Reduced from 60% */
  padding: 10px 43px 10px 13px; /* Reduced padding by 10% */
  font-size: 14.6px; /* Reduced from 16.2px */
  border: 1px solid #d9d9d9;
  border-top-right-radius: 6px; /* Reduced from 7px */
  border-bottom-right-radius: 6px;
  outline: none;
`;

const SuggestionsBox = styled.div`
  position: absolute;
  width: 96.5%; /* Reduced from 95% */
  background-color: white;
  left: 5px;
  border: 1px solid #ccc;
  border-radius: 6px; /* Reduced from 7px */
  max-height: 180px; /* Reduced from 200px */
  overflow-y: auto;
  z-index: 10;
  top: calc(100% + 4px);
`;

const SuggestionBoxContainer = styled.div`
  padding: 0px 7px; /* Reduced from 8px */
`;

const SuggestionItem = styled.div`
  padding: 9px; /* Reduced from 10px */
  cursor: pointer;
  &:hover {
    background-color: #f0f0f0;
  }
`;

const NoSuggestions = styled.div`
  padding: 9px; /* Reduced from 10px */
  color: #888;
`;

const DropdownItem = styled.div`
  padding: 9px; /* Reduced from 10px */
  color: #545f71;
  cursor: pointer;
  &:hover {
    background-color: #f0f0f0;
  }
  &:not(:last-child) {
    border-bottom: 1px solid #ccc;
  }
`;

const SearchInputAndDropdown = styled.div`
  display: flex;
  align-items: center;
  margin: 0 auto;
  margin-top: 11px; /* Reduced from 18px */
  position: relative;
  // padding: 14.4px;
  // width: 86.4%; /* Reduced from 96% */
`;

const DropdownContainer = styled.div`
  width: 36%; /* Reduced from 40% */
`;

const DropdownButton = styled.button`
  width: 100%;
  padding: 10px 3px; /* Reduced padding by 10% */
  font-size: 14.6px; /* Reduced from 16.2px */
  border: 1px solid #d9d9d9;
  border-top-left-radius: 6px; /* Reduced from 7px */
  border-bottom-left-radius: 6px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  background-color: #f0f0f0;
  cursor: pointer;
  display: flex;
  justify-content: space-around;
  align-items: center;
  color: #5e5d5d;
`;

const DropdownMenu = styled.div<{ showDropdown: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 100%; /* Reduced from 100% */
  background-color: white;
  border: 1px solid #ccc;
  border-radius: 6px; /* Reduced from 7px */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 10;
  display: ${(props) => (props.showDropdown ? "block" : "none")};
`;

const LoadingIcon = styled(FaSpinner)`
  position: absolute;
  right: 14px; /* Reduced from 16px */
  transform: translateY(-50%);
  color: ${(props) => props.theme.colors.primary};
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const SearchIcon = styled(GrSearch)`
  position: absolute;
  right: 14px; /* Reduced from 16px */
  top: 50%;
  transform: translateY(-50%);
  color: ${(props) => props.theme.colors.primary};
`;

const LoadMoreButton = styled.button`
  margin: 0px auto;
  background: white;
  color: #2b70f4;
  border: none;
  font-size: 14.4px; /* Reduced from 16px */
  border-radius: 3.6px; /* Reduced from 4px */
  cursor: pointer;
  box-shadow: 0px 3.6px 5.4px rgba(0, 0, 0, 0.09); /* Reduced from 4px 6px */
  padding: 9px 27px; /* Reduced padding by 10% */
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0px 5.4px 7.2px rgba(0, 0, 0, 0.13); /* Reduced from 6px 8px */
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ButtonBox = styled.div`
  display: flex;
  justify-content: center;
`;
const ClearIcon = styled(FaTimes)`
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: ${(props) => props.theme.colors.primary};
  cursor: pointer;
`;
